/**
 * POP template — vibrant block carousel.
 *
 * Sticky horizontal scroll. Products as color-block cards. 3D mesh per product
 * laid in a row, lerped position based on scroll progress. Pedestal + breath +
 * auto-spin on the active one. Mouse parallax + drag-to-rotate.
 *
 * Exposes the universal template contract:
 *   mount(canvas, products, look, host) → { setActive, setPaused, retintActive, destroy }
 */
import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { buildMesh, setupEnvironment, retintMesh, TONEMAPS } from '../../core/scene-utils.js';
import { el, isDarkHex, createDisposer } from '../../core/utils.js';
import { frames } from '../../look.js';
import { popLayout } from './layout.js';

export function mount(canvas, products, look, host, opts = {}) {
  const cfg = {
    fov: 38, cameraY: 0.3, cameraZ: 5.2, cameraTiltX: -0.08,
    exposure: 1.15, tonemap: 'aces', envIntensity: 0.9,
    keyLightColor: '#ffffff', keyLightIntensity: 1.0,
    rimLightColor: '#ff1493', rimLightIntensity: 1.2,
    groundShadow: true, spacing: 3.4,
    bloom: true, bloomStrength: 0.55, bloomRadius: 0.6, bloomThreshold: 0.7,
    mouseParallax: 0.18, activeScale: 1.18, activeLift: 0.12,
    hdriUrl: null, hdriAsBackground: false,
    ...look,
  };

  const disposer = createDisposer();

  // ---- DOM ----
  const dom = popLayout(host, { embedded: !!opts.embedded });
  const grid = dom.grid;

  // ---- Three.js ----
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = cfg.groundShadow;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = TONEMAPS[cfg.tonemap] ?? THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = cfg.exposure;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(cfg.fov, 1, 0.1, 100);
  camera.position.set(0, cfg.cameraY, cfg.cameraZ);
  camera.rotation.x = cfg.cameraTiltX;

  disposer.add(setupEnvironment(renderer, scene, cfg));

  scene.add(new THREE.AmbientLight(0xffffff, 0.25));
  const key = new THREE.DirectionalLight(cfg.keyLightColor, cfg.keyLightIntensity);
  key.position.set(3, 5, 4);
  key.castShadow = cfg.groundShadow;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.left = -4; key.shadow.camera.right = 4;
  key.shadow.camera.top = 4; key.shadow.camera.bottom = -4;
  key.shadow.radius = 6;
  scene.add(key);
  const rim = new THREE.PointLight(cfg.rimLightColor, cfg.rimLightIntensity, 12);
  rim.position.set(-3, -2, 2); scene.add(rim);

  if (cfg.groundShadow) {
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(40, 40),
      new THREE.ShadowMaterial({ opacity: 0.18 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -1.1;
    ground.receiveShadow = true;
    scene.add(ground);
  }

  const meshes = products.map((p, i) => {
    const m = buildMesh(p);
    m.position.x = i * cfg.spacing;
    m.userData.product = p;
    m.userData.scale = 1;
    m.userData.lift = 0;
    m.traverse((node) => { if (node.isMesh) { node.castShadow = cfg.groundShadow; node.receiveShadow = false; } });
    scene.add(m);
    return m;
  });

  // ---- Postprocessing ----
  let composer = null;
  if (cfg.bloom) {
    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      cfg.bloomStrength, cfg.bloomRadius, cfg.bloomThreshold,
    );
    composer.addPass(bloom);
    composer.addPass(new OutputPass());
  }

  // ---- Mouse / drag ----
  const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
  const onPointerMove = (e) => {
    mouse.tx = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.ty = -((e.clientY / window.innerHeight) * 2 - 1);
  };
  window.addEventListener('pointermove', onPointerMove, { passive: true });
  disposer.add(() => window.removeEventListener('pointermove', onPointerMove));

  const drag = { active: false, lastX: 0, lastY: 0, rotY: 0, rotX: 0, releaseAt: 0 };
  const onDown = (e) => { drag.active = true; drag.lastX = e.clientX; drag.lastY = e.clientY; canvas.setPointerCapture?.(e.pointerId); };
  const endDrag = (e) => { if (!drag.active) return; drag.active = false; drag.releaseAt = performance.now(); canvas.releasePointerCapture?.(e.pointerId); };
  const onDragMove = (e) => {
    if (!drag.active) return;
    const dx = e.clientX - drag.lastX, dy = e.clientY - drag.lastY;
    drag.lastX = e.clientX; drag.lastY = e.clientY;
    drag.rotY += dx * 0.01;
    drag.rotX = Math.max(-0.6, Math.min(0.6, drag.rotX + dy * 0.005));
  };
  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointerleave', endDrag);
  canvas.addEventListener('pointercancel', endDrag);
  window.addEventListener('pointermove', onDragMove);
  disposer.add(() => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointerup', endDrag);
    canvas.removeEventListener('pointerleave', endDrag);
    canvas.removeEventListener('pointercancel', endDrag);
    window.removeEventListener('pointermove', onDragMove);
  });

  // ---- State + framing ----
  const initialFrame = frames[products[0]?.framing] || { fov: cfg.fov, cameraY: cfg.cameraY, cameraZ: cfg.cameraZ, cameraTiltX: cfg.cameraTiltX };
  const frameState = { ...initialFrame };
  const frameTarget = { ...initialFrame };
  const state = { targetIndex: 0, currentX: 0 };

  function setActive(index) {
    state.targetIndex = Math.max(0, Math.min(products.length - 1, index));
    const f = frames[products[state.targetIndex]?.framing];
    if (f) Object.assign(frameTarget, f);
  }
  function retintActive(colors) {
    const m = meshes[state.targetIndex];
    if (!m) return;
    retintMesh(m, colors);
    const p = m.userData.product;
    if (p) {
      if (colors.colorA) p.colorA = colors.colorA;
      if (colors.colorB) p.colorB = colors.colorB;
      if (colors.accent) p.accent = colors.accent;
    }
  }

  let paused = false;
  function setPaused(v) { paused = !!v; }

  // ---- Resize ----
  function resize() {
    const w = window.innerWidth, h = window.innerHeight;
    renderer.setSize(w, h, false);
    if (composer) composer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  window.addEventListener('resize', resize);
  disposer.add(() => window.removeEventListener('resize', resize));
  resize();

  // ---- Tick ----
  let rafId = 0;
  function tick(t) {
    const targetX = state.targetIndex * cfg.spacing;
    state.currentX += (targetX - state.currentX) * 0.08;

    mouse.x += (mouse.tx - mouse.x) * 0.05;
    mouse.y += (mouse.ty - mouse.y) * 0.05;

    meshes.forEach((m, i) => {
      const dist = Math.abs(i - state.targetIndex);
      const proximity = Math.max(0, 1 - dist);

      const targetScale = 1 + (cfg.activeScale - 1) * proximity;
      const targetLift = cfg.activeLift * proximity;
      m.userData.scale += (targetScale - m.userData.scale) * 0.1;
      m.userData.lift += (targetLift - m.userData.lift) * 0.1;

      const breath = 1 + Math.sin(t * 0.002) * 0.015 * proximity;

      const isActive = i === state.targetIndex;
      const sinceRelease = (performance.now() - drag.releaseAt) / 1500;
      const autoSpinFactor = isActive ? Math.max(0, Math.min(1, drag.active ? 0 : sinceRelease)) : 1;

      if (isActive) {
        m.rotation.y += (drag.rotY - (m.userData.lastDragY || 0));
        m.rotation.x += (drag.rotX - (m.userData.lastDragX || 0));
        m.userData.lastDragY = drag.rotY;
        m.userData.lastDragX = drag.rotX;
      }

      m.position.x = i * cfg.spacing - state.currentX;
      m.position.y = m.userData.lift;
      m.scale.setScalar(m.userData.scale * breath);

      m.rotation.y += proximity * 0.006 * autoSpinFactor;
      m.rotation.y += Math.sin(t * 0.0005 + i) * 0.0005;
      m.rotation.x += (mouse.y * cfg.mouseParallax * 0.5 * autoSpinFactor - m.rotation.x) * 0.05 * autoSpinFactor;

      const sp = m.userData.spinner;
      if (sp) sp.rotation.z = -t * 0.002;
    });

    frameState.fov += (frameTarget.fov - frameState.fov) * 0.06;
    frameState.cameraY += (frameTarget.cameraY - frameState.cameraY) * 0.06;
    frameState.cameraZ += (frameTarget.cameraZ - frameState.cameraZ) * 0.06;
    frameState.cameraTiltX += (frameTarget.cameraTiltX - frameState.cameraTiltX) * 0.06;
    camera.fov = frameState.fov;
    camera.updateProjectionMatrix();
    camera.position.x = mouse.x * cfg.mouseParallax * 0.6;
    camera.position.y = frameState.cameraY + mouse.y * cfg.mouseParallax * 0.3;
    camera.position.z = frameState.cameraZ;
    camera.rotation.x = frameState.cameraTiltX;
    camera.lookAt(0, frameState.cameraY * 0.4, 0);

    if (!paused) {
      if (composer) composer.render();
      else renderer.render(scene, camera);
    }
    rafId = requestAnimationFrame(tick);
  }
  rafId = requestAnimationFrame(tick);
  disposer.add(() => cancelAnimationFrame(rafId));

  // ---- Scroll: pinned horizontal carousel ----
  const collection = host.querySelector('#collection');
  const track = grid;
  const isWide = window.matchMedia('(min-width: 721px)').matches;
  let scrollTrigger = null;

  if (isWide && products.length > 1) {
    const distance = () => track.scrollWidth - window.innerWidth + 80;
    const tween = gsap.to(track, {
      x: () => -distance(),
      ease: 'none',
      scrollTrigger: {
        trigger: collection,
        start: 'top top',
        end: () => '+=' + distance(),
        pin: true,
        scrub: 0.8,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate(self) {
          const idx = Math.round(self.progress * (products.length - 1));
          setActive(idx);
        },
      },
    });
    scrollTrigger = tween.scrollTrigger;
  } else {
    scrollTrigger = ScrollTrigger.create({
      trigger: collection,
      start: 'top center',
      end: 'bottom center',
      onUpdate(self) {
        if (!products.length) return;
        const idx = Math.round(self.progress * (products.length - 1));
        setActive(idx);
      },
    });
  }
  disposer.add(() => { scrollTrigger?.kill(); gsap.set(track, { clearProps: 'transform' }); });

  // ---- Card interactions (hover / click / variant / customize) ----
  const onGridClick = (e) => {
    const chip = e.target.closest?.('.variant-chip');
    if (chip) {
      e.stopPropagation();
      const card = chip.closest('.product-card');
      if (!card) return;
      const productIdx = Number(card.dataset.index);
      const variantIdx = Number(chip.dataset.variant);
      const product = products[productIdx];
      const variant = product?.variants?.[variantIdx];
      if (!variant) return;
      card.querySelectorAll('.variant-chip').forEach((c) => c.classList.remove('is-active'));
      chip.classList.add('is-active');
      setActive(productIdx);
      retintActive(variant);
      const textColor = isDarkHex(variant.colorA) ? '#fff8ec' : '#0a0a0a';
      card.style.background = variant.colorA;
      card.style.color = textColor;
      const tag = card.querySelector('.tag');
      if (tag && variant.accent) tag.style.color = variant.accent;
      if (variant.price) { const p = card.querySelector('[data-price]'); if (p) p.textContent = variant.price; }
      if (variant.stripeLink) { const b = card.querySelector('[data-buy]'); if (b) b.href = variant.stripeLink; }
      return;
    }
    const customizeBtn = e.target.closest?.('[data-customize]');
    if (customizeBtn) {
      e.stopPropagation();
      const card = customizeBtn.closest('.product-card');
      const drawer = card?.querySelector('.customize-drawer');
      if (drawer) drawer.hidden = !drawer.hidden;
      return;
    }
    const card = e.target.closest('.product-card');
    if (card) {
      setActive(Number(card.dataset.index));
    }
  };
  grid.addEventListener('click', onGridClick);

  const onGridInput = (e) => {
    const input = e.target;
    if (!input.matches?.('input[type="color"][data-slot]')) return;
    const card = input.closest('.product-card');
    if (!card) return;
    const idx = Number(card.dataset.index);
    const slot = input.dataset.slot;
    const value = input.value;
    setActive(idx);
    retintActive({ [slot]: value });
    if (slot === 'colorA') {
      const textColor = isDarkHex(value) ? '#fff8ec' : '#0a0a0a';
      card.style.background = value;
      card.style.color = textColor;
    }
    if (slot === 'accent') {
      const tag = card.querySelector('.tag');
      if (tag) tag.style.color = value;
    }
  };
  grid.addEventListener('input', onGridInput);

  disposer.add(() => {
    grid.removeEventListener('click', onGridClick);
    grid.removeEventListener('input', onGridInput);
  });

  // ---- Destroy ----
  function destroy() {
    disposer.run();
    meshes.forEach((m) => {
      m.traverse((n) => { if (n.isMesh) { n.geometry?.dispose?.(); n.material?.dispose?.(); } });
    });
    renderer.dispose();
    composer?.dispose?.();
    dom.cleanup();
  }

  return { setActive, setPaused, retintActive, destroy };
}
